<div class="tile">
  <h1 class="zero-text p-bigger-med mb-3">{{ 'trading-tools.easy-trading-matrix.headline' | transloco }}</h1>
  <web-design-system-notification
    color="blue"
    [headline]="'trading-tools.easy-trading-matrix.notification.headline' | transloco"
    [isDismissible]="true"
    cacheIdentifier="easy-trading-matrix-notification"
  >
    {{ 'trading-tools.easy-trading-matrix.notification.text' | transloco }}
  </web-design-system-notification>
  <ng-container *ngTemplateOutlet="underlyingSelection" />
  <ng-container *ngTemplateOutlet="matrix" />
</div>

<ng-template #underlyingSelection>
  <div class="zero-text p-base-reg mb-2 mt-3">{{ 'trading-tools.easy-trading-matrix.underlyings.headline' | transloco }}</div>
  <div class="nav-tabs">
    @for (segment of segments; track $index) {
      <div class="nav-item">
        <a class="nav-link cursor-pointer" [class.active]="activeUnderlyingSegment === segment" (click)="toggleUnderlyingsSegment(segment)">
          {{ 'trading-tools.easy-trading-matrix.underlyings.' + segment | transloco }}
        </a>
      </div>
    }
  </div>
  @if (tradingToolsFacade.selectedUnderlyingIsin$ | async; as selectedUnderlyingIsin) {
    <div class="underlyings-navigation d-flex align-items-center">
      <div class="cursor-pointer no-select" (click)="navigateLeft()">
        <web-design-system-icon name="chevron-3" />
      </div>
      <div class="underlyings d-flex gap">
        @if (tradingToolsFacade.loadingUnderlyings$ | async) {
          @for (i of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]; track $index) {
            <div class="loading-shimmer underlying"></div>
          }
        } @else {
          @for (underlying of tradingToolsFacade.underlyings$ | async; track underlying.isin) {
            <div>
              @if (underlying.isin === selectedUnderlyingIsin) {
                <div #activeUnderlying></div>
              }
              <div
                class="underlying"
                [class.active]="underlying.isin === selectedUnderlyingIsin"
                [title]="underlying.name"
                (click)="tradingToolsFacade.selectUnderlying(underlying.isin)"
              >
                <div class="d-flex justify-content-center">
                  @if (underlying.iconUrl) {
                    <img alt="icon" class="logo" [src]="underlying.iconUrl" />
                  } @else {
                    <div class="logo d-flex justify-content-center align-items-center">
                      <web-design-system-icon name="ranking" />
                    </div>
                  }
                </div>

                <div
                  class="d-flex justify-content-center zero-text p-xsmall-reg mt-2 text-nowrap"
                  [class.course-green]="underlying.perfPct > 0"
                  [class.course-red]="underlying.perfPct < 0"
                >
                  @if (underlying.perfPct !== undefined) {
                    {{ underlying.perfPct | number: '1.2-2' }} %
                  } @else {
                    &mdash;
                  }
                </div>
              </div>
              @if (selectedUnderlyingIsin === underlying.isin) {
                <div class="triangle"></div>
              }
            </div>
          }
        }
      </div>
      <div class="cursor-pointer no-select" (click)="navigateRight()">
        <web-design-system-icon name="chevron-1" />
      </div>
    </div>
  }
</ng-template>

<ng-template #matrix>
  @if (tradingToolsFacade.easyTradingMatrix$ | async; as matrix) {
    <div class="matrix mt-3">
      <div class="top d-flex gap-half mb-2">
        <div class="d-none d-lg-flex align-items-center justify-content-center side-lane cursor-pointer no-select" (click)="navigateLeft()">
          <web-design-system-icon name="chevron-3" />
        </div>
        <div class="main-lane">
          @if (tradingToolsFacade.activeUnderlying$ | async; as underlying) {
            <div class="d-block d-lg-flex justify-content-center align-items-center gap">
              <div class="d-flex justify-content-center gap-half">
                @if (underlying.iconUrl) {
                  <img alt="icon" class="logo" [src]="underlying.iconUrl" />
                }
                @if (tradingToolsFacade.loadingEasyTradingMatrix$ | async) {
                  <div class="loading-shimmer name"></div>
                } @else {
                  <div class="zero-text p-base-med">{{ underlying.name }}</div>
                }
              </div>
              <div class="d-flex justify-content-center gap-half">
                <div class="zero-text p-small-bold">
                  @if (underlying.lastPrice !== undefined) {
                    {{ underlying.lastPrice | priceScale: 2 : false : (underlying.isIndex ? 'PKT' : 'EUR') }}
                  } @else {
                    &mdash;
                  }
                </div>
                <div
                  class="zero-text p-small-reg"
                  [class.course-green]="underlying.perfAbs > 0"
                  [class.course-red]="underlying.perfAbs < 0"
                >
                  {{ underlying.perfPct > 0 ? '+' : ''
                  }}{{ underlying.perfAbs | priceScale: 2 : false : (underlying.isIndex ? 'PKT' : 'EUR') }}
                </div>
                <div
                  class="zero-text p-small-reg"
                  [class.course-green]="underlying.perfPct > 0"
                  [class.course-red]="underlying.perfPct < 0"
                >
                  @if (underlying.perfPct !== undefined) {
                    {{ underlying.perfPct > 0 ? '+' : '' }}{{ underlying.perfPct | number: '1.2-2' }} %
                  } @else {
                    &mdash;
                  }
                </div>
              </div>
            </div>
          }
          <div class="d-none d-lg-flex justify-content-between gap mt-3">
            <div class="left-col">
              <div class="zero-text p-xsmall-reg text-center mb-2">
                {{ 'trading-tools.easy-trading-matrix.matrix.increase-hint-start' | transloco }}
              </div>
              <div class="d-flex justify-content-around">
                <div class="zero-text p-small-med">6 %</div>
                <div class="zero-text p-small-med">4 %</div>
                <div class="zero-text p-small-med">2 %</div>
              </div>
            </div>
            <div class="right-col">
              <div class="zero-text p-xsmall-reg text-center mb-2">
                {{ 'trading-tools.easy-trading-matrix.matrix.decrease-hint-start' | transloco }}
              </div>
              <div class="d-flex justify-content-around">
                <div class="zero-text p-small-med">-2 %</div>
                <div class="zero-text p-small-med">-4 %</div>
                <div class="zero-text p-small-med">-6 %</div>
              </div>
            </div>
          </div>
          <div class="d-block d-lg-none">
            <div class="d-flex justify-content-between gap align-items-center mt-2">
              <button
                class="toggle long"
                [class.active]="currentInvestmentDirection === leverageProductType.CALL"
                (click)="currentInvestmentDirection = leverageProductType.CALL"
              >
                Long
              </button>
              <button
                class="toggle short"
                [class.active]="currentInvestmentDirection === leverageProductType.PUT"
                (click)="currentInvestmentDirection = leverageProductType.PUT"
              >
                Short
              </button>
            </div>
            <div class="d-block mt-2">
              @if (currentInvestmentDirection === leverageProductType.CALL) {
                <div class="d-flex justify-content-center">
                  <div class="zero-text p-xsmall-reg text-center mb-2">
                    {{ 'trading-tools.easy-trading-matrix.matrix.increase-hint-start' | transloco }}
                  </div>
                </div>
                <div class="d-flex">
                  <div class="side-lane"></div>
                  <div class="main-lane d-flex d-flex justify-content-around">
                    <div class="zero-text p-small-med">6 %</div>
                    <div class="zero-text p-small-med">4 %</div>
                    <div class="zero-text p-small-med">2 %</div>
                  </div>
                </div>
              } @else {
                <div class="d-flex justify-content-center">
                  <div class="zero-text p-xsmall-reg text-center mb-2">
                    {{ 'trading-tools.easy-trading-matrix.matrix.decrease-hint-start' | transloco }}
                  </div>
                </div>
                <div class="d-flex">
                  <div class="main-lane d-flex justify-content-around">
                    <div class="zero-text p-small-med">-2 %</div>
                    <div class="zero-text p-small-med">-4 %</div>
                    <div class="zero-text p-small-med">-6 %</div>
                  </div>
                  <div class="side-lane"></div>
                </div>
              }
            </div>
          </div>
        </div>
        <div
          class="d-none d-lg-flex align-items-center justify-content-center side-lane cursor-pointer no-select"
          (click)="navigateRight()"
        >
          <web-design-system-icon name="chevron-1" />
        </div>
      </div>

      <div class="derivatives" [class.opacity]="tradingToolsFacade.loadingEasyTradingMatrix$ | async">
        <div class="label long d-none d-lg-block">Long</div>
        <div class="label short d-none d-lg-block">Short</div>
        @for (derivative of matrix.derivatives; track derivative.targetLeverage; let i = $index) {
          <div class="derivative-row d-flex" [class.first]="i === 0">
            <div
              class="side-lane d-none d-lg-flex align-items-center gap-half justify-content-lg-end"
              [class.d-flex]="currentInvestmentDirection === leverageProductType.CALL"
            >
              <div class="leverage-button">
                <web-design-system-button
                  [fullwidth]="true"
                  size="small"
                  [routerLink]="tradeMaskPath"
                  [queryParams]="
                    derivative
                      | trackingQueryParams: leverageProductType.CALL : (tradingToolsFacade.selectedUnderlyingIsin$ | async) : 'button'
                  "
                  [disabled]="!derivative.call?.isin"
                  >{{ 'trading-tools.easy-trading-matrix.matrix.leverage' | transloco }}
                  @if (derivative.targetLeverage > 10) {
                    Max*
                  } @else if (derivative.call?.leverage) {
                    {{ derivative.call.leverage }}
                  } @else {
                    {{ derivative.targetLeverage }}
                  }
                </web-design-system-button>
              </div>
              <web-design-system-icon-button
                icon="info"
                size="small"
                type="grey-light"
                [ngbTooltip]="tooltip"
                tooltipClass="without-arrow"
                triggers="manual"
                autoClose="outside"
                #tCall="ngbTooltip"
                (click)="toggleTooltip(tCall, derivative, leverageProductType.CALL)"
              />
            </div>
            <div class="main-lane d-flex">
              <div
                class="cell d-none d-lg-flex justify-content-around align-items-center"
                [class.d-flex]="currentInvestmentDirection === leverageProductType.CALL"
              >
                <ng-container *ngTemplateOutlet="item; context: { value: derivative?.call?.scenarios['6']?.percentIncrease }" />
                <ng-container *ngTemplateOutlet="item; context: { value: derivative?.call?.scenarios['4']?.percentIncrease }" />
                <ng-container *ngTemplateOutlet="item; context: { value: derivative?.call?.scenarios['2']?.percentIncrease }" />
              </div>
              <div
                class="cell d-none d-lg-flex justify-content-around align-items-center"
                [class.d-flex]="currentInvestmentDirection === leverageProductType.PUT"
              >
                <ng-container *ngTemplateOutlet="item; context: { value: derivative?.put?.scenarios['2']?.percentIncrease }" />
                <ng-container *ngTemplateOutlet="item; context: { value: derivative?.put?.scenarios['4']?.percentIncrease }" />
                <ng-container *ngTemplateOutlet="item; context: { value: derivative?.put?.scenarios['6']?.percentIncrease }" />
              </div>
            </div>
            <div
              class="side-lane d-none d-lg-flex align-items-center gap-half justify-content-end justify-content-lg-start"
              [class.d-flex]="currentInvestmentDirection === leverageProductType.PUT"
            >
              <web-design-system-icon-button
                icon="info"
                size="small"
                type="grey-light"
                [ngbTooltip]="tooltip"
                tooltipClass="without-arrow"
                triggers="manual"
                autoClose="outside"
                #tPut="ngbTooltip"
                (click)="toggleTooltip(tPut, derivative, leverageProductType.PUT)"
              />
              <div class="leverage-button">
                <web-design-system-button
                  [fullwidth]="true"
                  size="small"
                  [routerLink]="tradeMaskPath"
                  [disabled]="!derivative.put?.isin"
                  [queryParams]="
                    derivative
                      | trackingQueryParams: leverageProductType.PUT : (tradingToolsFacade.selectedUnderlyingIsin$ | async) : 'button'
                  "
                  >{{ 'trading-tools.easy-trading-matrix.matrix.leverage' | transloco }}
                  @if (derivative.targetLeverage > 10) {
                    Max*
                  } @else if (derivative.put?.leverage) {
                    {{ derivative.put.leverage }}
                  } @else {
                    {{ derivative.targetLeverage }}
                  }
                </web-design-system-button>
              </div>
            </div>
          </div>
          <ng-template #tooltip let-product="product" let-derivative="derivative" let-type="type">
            <div class="zero-text p-small-bold mb-2 text-left">{{ product.name }}</div>
            <div class="d-flex justify-content-between align-items-center gap">
              <div class="zero-text p-small-reg">{{ 'trading-tools.easy-trading-matrix.matrix.tooltip.isin' | transloco }}</div>
              <div class="zero-text p-small-med text-nowrap">{{ product.isin ? product.isin : '&mdash;' }}</div>
            </div>
            <div class="d-flex justify-content-between align-items-center gap">
              <div class="zero-text p-small-reg">{{ 'trading-tools.easy-trading-matrix.matrix.tooltip.wkn' | transloco }}</div>
              <div class="zero-text p-small-med text-nowrap">{{ product.wkn ? product.wkn : '&mdash;' }}</div>
            </div>
            <div class="d-flex justify-content-between align-items-center gap">
              <div class="zero-text p-small-reg">{{ 'trading-tools.easy-trading-matrix.matrix.tooltip.issuer' | transloco }}</div>
              <div class="zero-text p-small-med text-nowrap">{{ product.issuerName ? product.issuerName : '&mdash;' }}</div>
            </div>
            <div class="d-flex justify-content-between align-items-center gap">
              <div class="zero-text p-small-reg">{{ 'trading-tools.easy-trading-matrix.matrix.tooltip.basePrice' | transloco }}</div>
              <div class="zero-text p-small-med text-nowrap">{{ product.strike ? product.strike : '&mdash;' }}</div>
            </div>
            <div class="d-flex justify-content-between align-items-center gap">
              <div class="zero-text p-small-reg">{{ 'trading-tools.easy-trading-matrix.matrix.tooltip.knockOut' | transloco }}</div>
              <div class="zero-text p-small-med text-nowrap">
                {{ product.knockoutBarrier ? product.knockoutBarrier : '&mdash;' }}
              </div>
            </div>
            @if (product.isin) {
              @if (tradingToolsFacade.selectedUnderlyingIsin$ | async; as underlyingIsin) {
                <div class="d-flex justify-content-end mt-2">
                  <web-components-instrument-details-link
                    linkCssClass="zero-text link"
                    [isin]="product.isin"
                    [queryParams]="derivative | trackingQueryParams: type : underlyingIsin : 'more'"
                  >
                    {{ 'trading-tools.easy-trading-matrix.matrix.tooltip.more-infos' | transloco }}
                  </web-components-instrument-details-link>
                </div>
              }
            }
          </ng-template>
        }
        @if (tradingToolsFacade.activeUnderlying$ | async; as underlying) {
          <div class="derivative-row d-none d-lg-flex">
            <div class="side-lane d-flex align-items-center justify-content-center zero-text link">
              <a
                [routerLink]="leverageSearchPath"
                [queryParams]="{
                  isin: underlying.isin,
                  name: underlying.name,
                  editLeverage: true,
                  investmentType: 'call',
                }"
                class="zero-text link"
              >
                {{ 'trading-tools.easy-trading-matrix.matrix.choose-leverage' | transloco }}
              </a>
            </div>
            <div class="main-lane d-flex">
              <div class="cell d-flex justify-content-around align-items-center zero-text p-xsmall-reg">
                {{ 'trading-tools.easy-trading-matrix.matrix.increase-hint-end' | transloco }}
              </div>
              <div class="cell last d-flex justify-content-around align-items-center zero-text p-xsmall-reg">
                {{ 'trading-tools.easy-trading-matrix.matrix.decrease-hint-end' | transloco }}
              </div>
            </div>
            <div class="side-lane d-flex align-items-center justify-content-center zero-text link">
              <a
                [routerLink]="leverageSearchPath"
                [queryParams]="{
                  isin: underlying.isin,
                  name: underlying.name,
                  editLeverage: true,
                  investmentType: 'put',
                }"
                class="zero-text link"
              >
                {{ 'trading-tools.easy-trading-matrix.matrix.choose-leverage' | transloco }}
              </a>
            </div>
          </div>
          <div class="d-block d-lg-none">
            @if (currentInvestmentDirection === leverageProductType.CALL) {
              <div class="d-flex justify-content-center zero-text p-xsmall-reg mt-3 mb-2">
                {{ 'trading-tools.easy-trading-matrix.matrix.increase-hint-end' | transloco }}
              </div>
              <div class="d-flex justify-content-center">
                <a
                  [routerLink]="leverageSearchPath"
                  [queryParams]="{
                    isin: underlying.isin,
                    name: underlying.name,
                    editLeverage: true,
                    investmentType: 'call',
                  }"
                  class="zero-text link"
                >
                  {{ 'trading-tools.easy-trading-matrix.matrix.choose-own-leverage' | transloco }}
                </a>
              </div>
            } @else {
              <div class="d-flex justify-content-center zero-text p-xsmall-reg mt-3 mb-2">
                {{ 'trading-tools.easy-trading-matrix.matrix.decrease-hint-end' | transloco }}
              </div>
              <div class="d-flex justify-content-center">
                <a
                  [routerLink]="leverageSearchPath"
                  [queryParams]="{
                    isin: underlying.isin,
                    name: underlying.name,
                    editLeverage: true,
                    investmentType: 'put',
                  }"
                  class="zero-text link"
                >
                  {{ 'trading-tools.easy-trading-matrix.matrix.choose-own-leverage' | transloco }}
                </a>
              </div>
            }
          </div>
        }
      </div>
    </div>
    <div class="mt-2 zero-text p-small-reg">{{ 'trading-tools.easy-trading-matrix.matrix.max-explanation' | transloco }}</div>
  }
</ng-template>

<ng-template #item let-value="value">
  <div class="zero-text p-small-reg text-nowrap" [class.course-green]="value">{{ value ? value : '&mdash;' }}</div>
</ng-template>
